import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Chip,
  Typography,
  Box,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DataElement } from '../../types/DataAsset';
import ColumnDetailDialog from './ColumnDetailDialog';

interface DataElementsTableProps {
  elements: DataElement[];
  tableName?: string;
  domain?: string;
  assetId?: string;
  onUpdate?: () => void;
}

type Order = 'asc' | 'desc';
type OrderBy = 'name' | 'assetType' | 'table';

/**
 * DataElementsTable Component
 * Displays data elements (columns/attributes) in a sortable, searchable table
 */
const DataElementsTable: React.FC<DataElementsTableProps> = ({ elements, tableName, domain, assetId, onUpdate }) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedElement, setSelectedElement] = useState<DataElement | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const handleElementClick = (element: DataElement) => {
    setSelectedElement(element);
    setDetailDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedElement(null);
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = elements.map((element) => element.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  // Filter elements based on search term
  const filteredElements = elements.filter((element) =>
    element.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    element.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    element.table?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort elements
  const sortedElements = [...filteredElements].sort((a, b) => {
    const aValue = a[orderBy] || '';
    const bValue = b[orderBy] || '';
    
    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const getAssetTypeIcon = (element: DataElement) => {
    if (element.isPrimaryKey) return '🔑';
    if (element.isForeignKey) return '🔗';
    return '📊';
  };

  const getAssetTypeColor = (assetType: string) => {
    switch (assetType.toLowerCase()) {
      case 'column':
        return 'primary';
      case 'attribute':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (elements.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          No data elements available for this asset.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ width: '100%', mb: 2 }}>
        {/* Search Bar */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search data elements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader aria-label="data elements table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  indeterminate={selected.length > 0 && selected.length < elements.length}
                  checked={elements.length > 0 && selected.length === elements.length}
                  onChange={handleSelectAllClick}
                  inputProps={{
                    'aria-label': 'select all data elements',
                  }}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'assetType'}
                  direction={orderBy === 'assetType' ? order : 'asc'}
                  onClick={() => handleRequestSort('assetType')}
                >
                  Asset Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'table'}
                  direction={orderBy === 'table' ? order : 'asc'}
                  onClick={() => handleRequestSort('table')}
                >
                  Table
                </TableSortLabel>
              </TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedElements.map((element, index) => {
              const isItemSelected = isSelected(element.name);
              const labelId = `data-element-checkbox-${index}`;

              return (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={element._id || element.name}
                  selected={isItemSelected}
                >
                  <TableCell padding="checkbox" onClick={(e) => { e.stopPropagation(); handleClick(element.name); }}>
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                      inputProps={{
                        'aria-labelledby': labelId,
                      }}
                    />
                  </TableCell>
                  <TableCell 
                    component="th" 
                    id={labelId} 
                    scope="row"
                    onClick={() => handleElementClick(element)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{getAssetTypeIcon(element)}</span>
                      <Typography 
                        variant="body2" 
                        fontWeight={600}
                        sx={{
                          color: '#003366',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {element.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={element.assetType}
                      size="small"
                      color={getAssetTypeColor(element.assetType)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
                      {element.table || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {element.description || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          Showing {sortedElements.length} of {elements.length} data elements
          {selected.length > 0 && ` • ${selected.length} selected`}
        </Typography>
      </Box>
    </Paper>

      {/* Column Detail Dialog - Unidatum Style */}
      <ColumnDetailDialog
        open={detailDialogOpen}
        onClose={handleCloseDetail}
        element={selectedElement}
        tableName={tableName}
        domain={domain}
        assetId={assetId}
        onUpdate={onUpdate}
      />
    </>
  );
};

export default DataElementsTable;
